import { useState, useEffect, useRef, useCallback } from "react";
import TaskViewPanel from "../MyTask/TaskViewPanel";
import { X, ChevronLeft, ChevronRight, Calendar, Loader2 } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import axiosInstance from "../../API/interceptor";
import TaskListDetailPanel from "./TaskListDetailPanel";

const GRAD     = "linear-gradient(135deg,#059669,#047857)";
const STATUS   = "completed";
const PER_PAGE = 50;

const CHART_COLORS = ["#6366F1","#8B5CF6","#EC4899","#F59E0B","#10B981","#3B82F6","#EF4444","#06B6D4","#84CC16","#F97316"];
const PRIO_COLOR   = { CRITICAL:"#DC2626",URGENT:"#EA580C",HIGH:"#D97706",MEDIUM:"#059669",LOW:"#6B7280",NORMAL:"#7C3AED" };

function colorize(obj) {
  return Object.entries(obj||{}).map(([name,value],i) => ({
    name, value:Number(value)||0, color:CHART_COLORS[i%CHART_COLORS.length]
  })).filter(d => d.value > 0);
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return String(d.getDate()).padStart(2,"0")+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+d.getFullYear();
}

const STATUS_PILLS = [
  { label:"Total",    key: "_total"           },
  { label:"Open",     key: "open"             },
  { label:"Progress", key: "in progress"      },
  { label:"On Hold",  key: "on hold"          },
  { label:"Pend.",    key: "approval pending" },
  { label:"Complete", key: "completed"        },
];

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-[14px] border-[1.5px] border-[#F1F5F9] px-4 py-3.5 mb-3.5">
      <p className="m-0 mb-2.5 text-[11px] font-extrabold uppercase tracking-[.8px] text-[#94A3B8]">{title}</p>
      {children}
    </div>
  );
}

function PieLegend({ data, onClickItem }) {
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
      {data.map(d => (
        <div key={d.name} onClick={() => onClickItem?.(d.name)}
          className="flex items-center gap-1.5 px-1 py-0.5 rounded-[5px] transition-colors duration-[120ms] cursor-pointer hover:bg-[#F1F5F9]">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
          <span className="text-[10px] text-[#64748B] font-medium flex-1 overflow-hidden whitespace-nowrap text-ellipsis">{d.name}</span>
          <span className="text-[10px] font-bold text-[#374151]">{d.value}</span>
        </div>
      ))}
    </div>
  );
}

function StatusPills({ totalTasks, panelStatus }) {
  const total = totalTasks || 0;
  return (
    <div className="flex gap-2 flex-wrap mt-2">
      {STATUS_PILLS.map(({ label, key }) => {
        const count = (key === "_total" || key === panelStatus) ? total : 0;
        return (
          <div key={label} className="flex items-center gap-1.5 px-3 py-[5px] rounded-lg bg-white/15 border border-white/25">
            <span className="text-[11px] font-semibold text-white whitespace-nowrap">{label}</span>
            <span className="text-[11px] font-extrabold px-[7px] py-px rounded-[5px] bg-white/25 text-white">
              {count > 0 ? count.toLocaleString() : "00"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Pagination({ page, totalPages, totalTasks, perPage, onChange }) {
  if (totalPages <= 1) return null;
  const start = (page - 1) * perPage + 1;
  const end   = Math.min(page * perPage, totalTasks);

  const nums = () => {
    const arr = []; const delta = 2;
    const l = Math.max(2, page - delta); const r = Math.min(totalPages - 1, page + delta);
    arr.push(1);
    if (l > 2) arr.push("...");
    for (let i = l; i <= r; i++) arr.push(i);
    if (r < totalPages - 1) arr.push("...");
    if (totalPages > 1) arr.push(totalPages);
    return arr;
  };

  const Btn = ({ disabled, onClick, children, active }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`min-w-8 h-8 rounded-lg text-xs font-semibold inline-flex items-center justify-center px-2 gap-[3px] border-[1.5px] transition-all duration-150 ${
        active
          ? "border-[#6366F1] bg-[#6366F1] text-white cursor-pointer"
          : disabled
          ? "border-[#E2E8F0] bg-[#F8FAFC] text-[#CBD5E1] cursor-not-allowed"
          : "border-[#E2E8F0] bg-white text-[#374151] cursor-pointer"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="flex items-center justify-between pt-3 pb-0.5 flex-wrap gap-2">
      <span className="text-xs text-[#64748B]">
        Showing <strong className="text-[#374151]">{start.toLocaleString()}–{end.toLocaleString()}</strong> of{" "}
        <strong className="text-[#374151]">{totalTasks.toLocaleString()}</strong> tasks
      </span>
      <div className="flex gap-1 items-center flex-wrap">
        <Btn disabled={page===1} onClick={() => onChange(page-1)}><ChevronLeft size={12}/> Prev</Btn>
        {nums().map((p,i) =>
          p==="..."
            ? <span key={`d${i}`} className="text-xs text-[#94A3B8] px-1">…</span>
            : <Btn key={p} active={p===page} onClick={() => onChange(p)}>{p}</Btn>
        )}
        <Btn disabled={page===totalPages} onClick={() => onChange(page+1)}>Next <ChevronRight size={12}/></Btn>
      </div>
    </div>
  );
}

const COLS = [
  { key:"unique_seq_id", label:"ID",        w:90  },
  { key:"task_type",     label:"TASK",      w:200, sticky:"left" },
  { key:"priority",      label:"PRIORITY",  w:100 },
  { key:"task_status",   label:"STATUS",    w:120 },
  { key:"product_name",  label:"WORKSPACE", w:150 },
  { key:"responsible",   label:"RESP.",     w:160 },
  { key:"deadline",      label:"DEADLINE",  w:140, sticky:"right" },
];

function TaskTable({ tasks, onRowClick, tableLoading, page, totalPages, totalTasks, perPage, onPageChange }) {
  const scrollRef = useRef();
  const drag = useRef({ active:false, startX:0, scrollLeft:0 });
  const onMD = e => { drag.current={active:true,startX:e.pageX-scrollRef.current.offsetLeft,scrollLeft:scrollRef.current.scrollLeft}; scrollRef.current.style.cursor="grabbing"; };
  const onMU = () => { drag.current.active=false; if(scrollRef.current) scrollRef.current.style.cursor="grab"; };
  const onMM = e => { if(!drag.current.active) return; e.preventDefault(); scrollRef.current.scrollLeft=drag.current.scrollLeft-(e.pageX-scrollRef.current.offsetLeft-drag.current.startX); };

  return (
    <div className="rounded-[10px] border border-[#F1F5F9] overflow-hidden">
      <div ref={scrollRef} className="overflow-x-auto cursor-grab"
        onMouseDown={onMD} onMouseUp={onMU} onMouseLeave={onMU} onMouseMove={onMM}>
        <table className="w-full border-separate border-spacing-0 text-[12px]">
          <thead>
            <tr>{COLS.map(c => (
              <th key={c.key}
                className="px-3 py-[10px] text-left text-[10px] font-bold text-[#94A3B8] tracking-[.8px] uppercase bg-[#F8FAFC] border-b-2 border-[#F1F5F9] whitespace-nowrap"
                style={{ position:c.sticky?"sticky":"static", left:c.sticky==="left"?0:"auto", right:c.sticky==="right"?0:"auto", zIndex:c.sticky?4:1, minWidth:c.w }}>
                {c.label}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr><td colSpan={COLS.length}>
                <div className="flex items-center justify-center h-[120px] gap-2.5">
                  <Loader2 size={18} color="#6366F1" className="animate-spin"/>
                  <span className="text-[12px] text-[#94A3B8]">Loading page {page}…</span>
                </div>
              </td></tr>
            ) : tasks.length === 0 ? (
              <tr><td colSpan={COLS.length} className="py-10 text-center text-[#CBD5E1] text-[13px]">No tasks found</td></tr>
            ) : tasks.map((t, i) => {
              const bg   = i%2===0?"#fff":"#FAFBFE";
              const prio = (t.priority||"").toUpperCase();
              const stat = (t.task_status||"").toLowerCase();
              const ov   = t.completion_sla && new Date(t.completion_sla)<new Date() && !["completed","closed"].includes(stat);
              return (
                <tr key={t._id||i}
                  onMouseEnter={e=>e.currentTarget.querySelectorAll("td").forEach(td=>td.style.background="#EEF2FF")}
                  onMouseLeave={e=>e.currentTarget.querySelectorAll("td").forEach(td=>td.style.background=i%2===0?"#fff":"#FAFBFE")}>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9]" style={{background:bg}}>
                    <span className="font-mono text-[11px] bg-[#EEF2FF] text-[#4338CA] px-1.5 py-px rounded-[5px] font-semibold">#{t.unique_seq_id||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9] sticky left-0 z-[2]" style={{background:bg}}>
                    <span onClick={()=>onRowClick(t._id)} className="text-[#4F46E5] font-semibold cursor-pointer whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis block hover:underline">{t.task_type||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9]" style={{background:bg}}>
                    <span className="text-[11px] font-bold px-2 py-px rounded-full" style={{background:(PRIO_COLOR[prio]||"#94A3B8")+"18",color:PRIO_COLOR[prio]||"#94A3B8"}}>{prio||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9]" style={{background:bg}}>
                    <span className="text-[11px] px-2 py-px rounded-full bg-[#EEF2FF] text-[#4338CA]">{t.task_status||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9]" style={{background:bg}}>
                    <span className="text-[11px] bg-[#F1F5F9] text-[#475569] px-2 py-px rounded-[6px]">{t.product_name||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9]" style={{background:bg}}>
                    <span className="text-[11px] text-[#374151]">{t.responsible||"—"}</span>
                  </td>
                  <td className="px-3 py-[10px] border-b border-[#F1F5F9] sticky right-0 z-[2] border-l border-[#EEF0F4]" style={{background:bg}}>
                    <span className="text-[11px] font-semibold px-[9px] py-[3px] rounded-lg flex items-center gap-1 whitespace-nowrap" style={{background:ov?"#FEE2E2":"#F0FDF4",color:ov?"#DC2626":"#059669"}}>
                      <Calendar size={10}/>{t.deadline || "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 pb-3 border-t border-[#F1F5F9] bg-[#FAFBFE]">
        <Pagination page={page} totalPages={totalPages} totalTasks={totalTasks} perPage={perPage} onChange={onPageChange}/>
      </div>
    </div>
  );
}

function DetailPopup({ title, filterBody, onClose, onTaskClick }) {
  const [loading, setLoading] = useState(false);
  const [tasks,   setTasks]   = useState([]);
  useEffect(() => {
    if (!filterBody) return;
    setLoading(true);
    axiosInstance.post("/task_assignment/get_task_list_detail", filterBody)
      .then(r => { if(r.data?.success) setTasks(r.data.data?.tasks||[]); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [JSON.stringify(filterBody)]); // eslint-disable-line

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-[1401] bg-[rgba(15,23,42,.25)] backdrop-blur-sm"/>
      <div onClick={e=>e.stopPropagation()} className="fixed top-0 right-0 bottom-0 w-[min(1350px,96vw)] bg-[#F8FAFC] z-[1402] flex flex-col shadow-[-8px_0_32px_rgba(0,0,0,.15)] animate-[slideInRight_.3s_cubic-bezier(.4,0,.2,1)]">
        <div className="shrink-0 px-5 pt-[14px] pb-4" style={{background:GRAD}}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="flex items-center gap-1 bg-transparent border-0 cursor-pointer text-white/70 text-[12px] p-0"><ChevronLeft size={14}/> Back</button>
              <ChevronRight size={12} color="rgba(255,255,255,.4)"/>
              <span className="text-white text-[13px] font-bold">{title}</span>
            </div>
            <button onClick={onClose} className="w-[30px] h-[30px] rounded-lg flex items-center justify-center bg-white/[.12] border border-white/20 cursor-pointer"><X size={13} color="#fff"/></button>
          </div>
          <StatusPills totalTasks={totalTasks} panelStatus={STATUS}/>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading
            ? <div className="flex items-center justify-center h-[200px] gap-2"><Loader2 size={20} color="#6366F1" className="animate-spin"/><span className="text-[12px] text-[#94A3B8]">Loading…</span></div>
            : <TaskTable tasks={tasks} onRowClick={onTaskClick} tableLoading={false}
                page={1} totalPages={1} totalTasks={tasks.length} perPage={tasks.length} onPageChange={()=>{}}/>
          }
        </div>
      </div>
    </>
  );
}

export default function CompletedPanel({ open, onClose, filterBody }) {
  const [loading,        setLoading]        = useState(false);
  const [tableLoading,   setTableLoading]   = useState(false);
  const [data,           setData]           = useState(null);
  const [chartsCache,    setChartsCache]    = useState(null);
  const [page,           setPage]           = useState(1);
  const [totalTasks,     setTotalTasks]     = useState(0);
  const [totalPages,     setTotalPages]     = useState(1);
  const [statusSummary,  setStatusSummary]  = useState({});   // ← all-task status counts for pills
  const [level2,         setLevel2]         = useState(null);
  const [taskId,         setTaskId]         = useState(null);

  // pills use total_tasks from first API call (all tasks, not current page)
  const totalTasksForPills = totalTasks;

  const doFetch = useCallback((pg, isPageChange = false) => {
    if (isPageChange) setTableLoading(true);
    else { setLoading(true); setData(null); }

    axiosInstance.post("/task_assignment/get_status_drilldown", {
      ...(filterBody || {}),
      status:   STATUS,
      page:     pg,
      per_page: PER_PAGE,
    })
    .then(r => {
      if (r.data?.success) {
        const d = r.data.data;
        setTotalTasks(d.total_tasks || 0);
        setTotalPages(d.total_pages || 1);
        // ── FIX: always update status_summary (comes from all tasks, not page) ──
        if (d.status_summary) setStatusSummary(d.status_summary);
        if (pg === 1) {
          setData(d);
          setChartsCache(d);  // charts use all-task aggregation from backend
        } else {
          setData(prev => prev ? { ...prev, tasks: d.tasks || [] } : d);
        }
      }
    })
    .catch(console.error)
    .finally(() => { setLoading(false); setTableLoading(false); });
  }, [filterBody]); // eslint-disable-line

  // Reset + fetch when panel opens or filter changes
  useEffect(() => {
    if (!open) { setPage(1); return; }
    setPage(1);
    doFetch(1, false);
  }, [open, JSON.stringify(filterBody)]); // eslint-disable-line

  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
    doFetch(newPage, true);
  }, [page, totalPages, doFetch]);

  const openL2 = useCallback((title, extra) => {
    setLevel2({ title, filterBody: { ...(filterBody||{}), status:STATUS, ...extra } });
  }, [filterBody]);

  // Use cached chart data from page 1 (accurate, based on all matching tasks)
  const chartSrc     = chartsCache || data;
  const productData  = colorize(chartSrc?.by_product);
  const categoryData = colorize(chartSrc?.by_category);
  const priorityData = colorize(chartSrc?.by_priority);
  const userData     = colorize(chartSrc?.by_user);
  const tasks        = data?.tasks || [];

  return (
    <>
      {open && <div onClick={onClose} className="fixed inset-0 z-[1299] bg-[rgba(15,23,42,.35)] backdrop-blur-sm"/>}
      <div onClick={e=>e.stopPropagation()}
        className={`fixed top-0 right-0 bottom-0 w-[min(1500px,97vw)] bg-[#F8FAFC] z-[1300] flex flex-col transition-transform duration-[350ms] ease-[cubic-bezier(.4,0,.2,1)] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}>

        {level2 && <div className="absolute inset-0 bg-[rgba(15,23,42,.2)] backdrop-blur-[3px] z-10 pointer-events-none"/>}

        {/* Header */}
        <div className="shrink-0" style={{background:GRAD}}>
          <div className="flex items-center justify-between px-5 pt-[14px] pb-[10px]">
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="flex items-center gap-1 bg-transparent border-0 cursor-pointer text-white/70 text-[12px] p-0"><ChevronLeft size={14}/> Dashboard</button>
              <ChevronRight size={12} color="rgba(255,255,255,.4)"/>
              <span className="text-white/70 text-[12px]">Analytics Meter</span>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[.12] border border-white/20 cursor-pointer"><X size={14} color="#fff"/></button>
          </div>
          <div className="px-5 pb-[18px]">
            <h1 className="text-white text-[18px] font-extrabold m-0 mb-[2px]">
              {STATUS.charAt(0).toUpperCase()+STATUS.slice(1)} Tasks
            </h1>
            <p className="text-white/65 text-[12px] m-0">
              {loading ? "Loading…" : `${totalTasks.toLocaleString()} tasks found`}
            </p>
            <StatusPills totalTasks={totalTasks} panelStatus={STATUS}/>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-[300px] gap-2.5">
              <Loader2 size={24} color="#6366F1" className="animate-spin"/>
              <span className="text-[13px] text-[#94A3B8]">Loading…</span>
            </div>
          ) : data ? (
            <>
              <div className="grid grid-cols-2 gap-3.5 mb-3.5">
                <SectionCard title="Workspace Distribution — click to drill down">
                  <div className="flex items-center gap-3">
                    <ResponsiveContainer width={110} height={110}>
                      <PieChart><Pie data={productData} cx="50%" cy="50%" innerRadius={28} outerRadius={48} dataKey="value" paddingAngle={2} style={{cursor:"pointer"}} onClick={d=>openL2(d.name,{product_name:d.name})}>{productData.map((d,i)=><Cell key={i} fill={d.color}/>)}</Pie><Tooltip contentStyle={{fontSize:11,borderRadius:8}}/></PieChart>
                    </ResponsiveContainer>
                    <PieLegend data={productData} onClickItem={n=>openL2(n,{product_name:n})}/>
                  </div>
                </SectionCard>
                <SectionCard title="Functional Categories — click to drill down">
                  <div className="flex items-center gap-3">
                    <ResponsiveContainer width={110} height={110}>
                      <PieChart><Pie data={categoryData} cx="50%" cy="50%" innerRadius={28} outerRadius={48} dataKey="value" paddingAngle={2} style={{cursor:"pointer"}} onClick={d=>openL2(d.name,{category_name:d.name})}>{categoryData.map((d,i)=><Cell key={i} fill={d.color}/>)}</Pie><Tooltip contentStyle={{fontSize:11,borderRadius:8}}/></PieChart>
                    </ResponsiveContainer>
                    <PieLegend data={categoryData} onClickItem={n=>openL2(n,{category_name:n})}/>
                  </div>
                </SectionCard>
              </div>
              <div className="grid grid-cols-2 gap-3.5 mb-3.5">
                <SectionCard title="Priority Breakdown — click to drill down">
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={priorityData} barSize={18} margin={{top:4,right:8,left:-20,bottom:0}}>
                      <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                      <XAxis dataKey="name" tick={{fontSize:9,fill:"#94A3B8"}} tickLine={false} axisLine={false}/>
                      <YAxis tick={{fontSize:9,fill:"#94A3B8"}} tickLine={false} axisLine={false}/>
                      <Tooltip contentStyle={{fontSize:11,borderRadius:8}}/>
                      <Bar dataKey="value" name="Tasks" radius={[4,4,0,0]} cursor="pointer" onClick={d=>openL2(d.name,{filter_type:"priority",filter_value:d.name})}>{priorityData.map((d,i)=><Cell key={i} fill={PRIO_COLOR[d.name.toUpperCase()]||d.color}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
                <SectionCard title="Assigned Users — click to drill down">
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={userData} barSize={18} margin={{top:4,right:8,left:-20,bottom:0}}>
                      <CartesianGrid vertical={false} stroke="#F1F5F9" strokeDasharray="3 3"/>
                      <XAxis dataKey="name" tick={{fontSize:9,fill:"#94A3B8"}} tickLine={false} axisLine={false}/>
                      <YAxis tick={{fontSize:9,fill:"#94A3B8"}} tickLine={false} axisLine={false}/>
                      <Tooltip contentStyle={{fontSize:11,borderRadius:8}}/>
                      <Bar dataKey="value" name="Tasks" radius={[4,4,0,0]} cursor="pointer" onClick={d=>openL2(d.name,{filter_type:"assigned_user",filter_value:d.name})}>{userData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
              </div>

              <SectionCard title={`Tasks — ${totalTasks.toLocaleString()} total`}>
                <TaskTable
                  tasks={tasks}
                  onRowClick={setTaskId}
                  tableLoading={tableLoading}
                  page={page}
                  totalPages={totalPages}
                  totalTasks={totalTasks}
                  perPage={PER_PAGE}
                  onPageChange={handlePageChange}
                />
              </SectionCard>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-[#F8FAFC] flex items-center justify-between shrink-0 border-t-[1.5px] border-[#F1F5F9]">
          <span className="text-[12px] text-[#94A3B8]">
            {totalTasks > 0 && (
              <>
                <strong className="text-[#374151]">{totalTasks.toLocaleString()}</strong> tasks total
                {totalPages > 1 && <> &nbsp;·&nbsp; Page <strong className="text-[#374151]">{page}</strong> of <strong className="text-[#374151]">{totalPages}</strong></>}
              </>
            )}
          </span>
          <button onClick={onClose} className="px-[22px] py-2 rounded-[10px] border-0 cursor-pointer text-white text-[12px] font-bold" style={{background:GRAD}}>Close</button>
        </div>
      </div>

      <TaskListDetailPanel open={!!level2} title={level2?.title || ""} filterBody={level2?.filterBody || {}} onClose={()=>setLevel2(null)}/>
      <TaskViewPanel taskId={taskId} onClose={()=>setTaskId(null)}/>
      <style>{"@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}"}</style>
    </>
  );
}